import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop
 * -------------------------------------------------------------------
 * Resets the window scroll on route change so a fresh navigation
 * (e.g. `/theoracle` → `/`) always lands at the top of the new page
 * — unless the destination URL carries a hash anchor (e.g. `/#lore`).
 *
 * When a hash IS present, we wait for the target section to appear in
 * the DOM (sections can mount lazily after the route change) and then
 * smooth-scroll it into view. This is what makes the header nav items
 * (Collection · About · Utility · Games · Lore) work correctly from
 * any secondary route, not just from the landing page itself.
 *
 * Reruns on both pathname AND hash changes so re-clicking the same
 * anchor from a different scroll position still fires.
 * -------------------------------------------------------------------
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Anchor navigation → wait for the target to exist, then smooth-scroll.
    if (hash) {
      let cancelled = false;
      const id = decodeURIComponent(hash.slice(1));
      // Try up to ~1s (60 frames) so lazy sections have time to mount.
      let tries = 0;
      const tick = () => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (tries++ < 60) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      return () => { cancelled = true; };
    }

    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  return null;
}
